/**
 * StepForm.tsx — the Step settings form (design doc §"Step settings form",
 * ADR-084 D1 `Step`), select-to-edit target from `ScriptsTab.tsx`'s step
 * list. `advance_when` is NOT editable this slice (S4 scope) — it is
 * preserved unchanged on the object via the `...step` spreads below.
 *
 * `required_facts` reuses the shared `ReorderableList` (design doc: "the
 * same shared reorder component as the step list"), rendering `facts[].label`
 * and backed by the ordered key array; `allowed_tools` is a closed checkbox
 * group over the document's own `tools[]` (D4 rule 4 — a tenant cannot
 * invent a tool), each checkbox individually labelled (accessibility notes).
 */

import { useTranslation } from 'react-i18next';
import {
  AGENT_CONFIG_MAX_LENGTHS,
  type FactDef,
  type SkillVersionFieldError,
  type Step,
} from '../../api/types';
import { CounterField, textActionClass, textActionDangerClass } from './agenticFieldHelpers';
import { ReorderableList } from './ReorderableList';
import { inputClass } from '../../components/Field';

export interface StepFormProps {
  scriptKey: string;
  step: Step;
  onChange: (next: Step) => void;
  onRemove: () => void;
  onClose: () => void;
  disabled?: boolean;
  facts: FactDef[];
  tools: string[];
  fieldErrors: SkillVersionFieldError[];
  /** True when this step_id collides with another step's id elsewhere in the document (D4 rule 1, client pre-check). */
  duplicateStepId: boolean;
}

const errorFor = (fieldErrors: SkillVersionFieldError[], path: string): string | undefined =>
  fieldErrors.find((error) => error.field_path === path)?.reason;

export const StepForm = ({
  scriptKey,
  step,
  onChange,
  onRemove,
  onClose,
  disabled = undefined,
  facts,
  tools,
  fieldErrors,
  duplicateStepId,
}: StepFormProps) => {
  const { t } = useTranslation();
  const path = (field: string) => `scripts[${scriptKey}].steps[${step.step_id}].${field}`;

  const availableFacts = facts.filter((fact) => !step.required_facts.includes(fact.key));
  const factLabel = (key: string) => facts.find((fact) => fact.key === key)?.label || key;

  const toggleTool = (tool: string, checked: boolean) =>
    onChange({
      ...step,
      allowed_tools: checked
        ? [...step.allowed_tools, tool]
        : step.allowed_tools.filter((name) => name !== tool),
    });

  return (
    <div
      aria-live="polite"
      data-testid={`agentic-step-${step.step_id}-editor`}
      className="flex flex-col gap-3 rounded-lg bg-surface-2 p-4 ring-1 ring-inset ring-ink-3/10"
    >
      <h4 className="text-body font-medium text-ink-1">
        {t('config.editAgentic.scripts.editingHeading', { stepId: step.step_id })}
      </h4>

      <div className="flex flex-col gap-1">
        <label
          htmlFor={`agentic-step-${step.step_id}-id`}
          className="text-meta font-medium text-ink-2"
        >
          {t('config.editAgentic.scripts.stepId')}
        </label>
        <input
          id={`agentic-step-${step.step_id}-id`}
          data-testid={`agentic-step-${step.step_id}-id`}
          type="text"
          value={step.step_id}
          disabled={disabled}
          onChange={(event) => onChange({ ...step, step_id: event.target.value })}
          className={`${inputClass} w-full font-mono`}
        />
        {duplicateStepId || errorFor(fieldErrors, path('step_id')) != null ? (
          <p
            role="alert"
            data-testid={`agentic-step-${step.step_id}-id-error`}
            className="text-meta text-status-down"
          >
            {errorFor(fieldErrors, path('step_id')) ??
              t('config.editAgentic.scripts.duplicateStepId')}
          </p>
        ) : null}
      </div>

      <CounterField
        id={`agentic-step-${step.step_id}-goal`}
        label={t('config.editAgentic.scripts.goal')}
        value={step.goal}
        maxLength={AGENT_CONFIG_MAX_LENGTHS.stepGoal}
        onChange={(next) => onChange({ ...step, goal: next })}
        disabled={disabled}
        multiline
        expandable
        error={errorFor(fieldErrors, path('goal'))}
      />

      <div className="flex flex-col gap-1">
        <span className="text-meta font-medium text-ink-2">
          {t('config.editAgentic.scripts.requiredFacts')}
        </span>
        {step.required_facts.length === 0 ? (
          <p
            data-testid={`agentic-step-${step.step_id}-facts-empty`}
            className="text-meta text-ink-3"
          >
            {t('config.editAgentic.scripts.requiredFactsEmpty')}
          </p>
        ) : (
          <ReorderableList
            items={step.required_facts}
            getId={(key) => key}
            onReorder={(next) => onChange({ ...step, required_facts: next })}
            ariaLabel={t('config.editAgentic.scripts.requiredFacts')}
            disabled={disabled}
            renderItem={(key) => {
              const orphaned =
                errorFor(fieldErrors, path('required_facts')) != null &&
                !facts.some((f) => f.key === key);
              return (
                <div className="flex flex-1 flex-wrap items-center gap-2">
                  <span className={`text-body ${orphaned ? 'text-status-down' : 'text-ink-1'}`}>
                    {factLabel(key)}
                  </span>
                  {orphaned ? (
                    <span
                      role="alert"
                      data-testid={`agentic-step-${step.step_id}-fact-${key}-orphaned`}
                      className="text-meta text-status-down"
                    >
                      {t('config.editAgentic.scripts.orphanedFact')}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    data-testid={`agentic-step-${step.step_id}-fact-${key}-remove`}
                    disabled={disabled}
                    onClick={() =>
                      onChange({
                        ...step,
                        required_facts: step.required_facts.filter((k) => k !== key),
                      })
                    }
                    className={textActionDangerClass}
                  >
                    {t('config.editAgentic.facts.remove')}
                  </button>
                </div>
              );
            }}
          />
        )}
        {availableFacts.length > 0 ? (
          <select
            data-testid={`agentic-step-${step.step_id}-facts-add`}
            aria-label={t('config.editAgentic.scripts.addFact')}
            disabled={disabled}
            value=""
            onChange={(event) => {
              const key = event.target.value;
              if (key !== '') onChange({ ...step, required_facts: [...step.required_facts, key] });
            }}
            className={inputClass}
          >
            <option value="">{t('config.editAgentic.scripts.addFact')}</option>
            {availableFacts.map((fact) => (
              <option key={fact.key} value={fact.key}>
                {fact.label || fact.key}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <fieldset className="flex flex-col gap-1">
        <legend className="text-meta font-medium text-ink-2">
          {t('config.editAgentic.scripts.allowedTools')}
        </legend>
        {errorFor(fieldErrors, path('allowed_tools')) != null ? (
          <p
            role="alert"
            data-testid={`agentic-step-${step.step_id}-tools-error`}
            className="text-meta text-status-down"
          >
            {errorFor(fieldErrors, path('allowed_tools'))}
          </p>
        ) : null}
        {tools.length === 0 ? (
          <p className="text-meta text-ink-3">{t('config.editAgentic.scripts.noTools')}</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {tools.map((tool) => (
              <label
                key={tool}
                htmlFor={`agentic-step-${step.step_id}-tool-${tool}`}
                className="flex items-center gap-1.5 text-meta text-ink-2"
              >
                <input
                  type="checkbox"
                  id={`agentic-step-${step.step_id}-tool-${tool}`}
                  data-testid={`agentic-step-${step.step_id}-tool-${tool}`}
                  aria-label={tool}
                  checked={step.allowed_tools.includes(tool)}
                  disabled={disabled}
                  onChange={(event) => toggleTool(tool, event.target.checked)}
                />
                {tool}
              </label>
            ))}
          </div>
        )}
      </fieldset>

      <CounterField
        id={`agentic-step-${step.step_id}-exit-when`}
        label={t('config.editAgentic.scripts.exitWhen')}
        value={step.exit_when}
        maxLength={AGENT_CONFIG_MAX_LENGTHS.stepExitWhen}
        onChange={(next) => onChange({ ...step, exit_when: next })}
        disabled={disabled}
        multiline
        expandable
        rows={3}
        error={errorFor(fieldErrors, path('exit_when'))}
      />
      <CounterField
        id={`agentic-step-${step.step_id}-on-decline`}
        label={t('config.editAgentic.scripts.onDecline')}
        value={step.on_decline}
        maxLength={AGENT_CONFIG_MAX_LENGTHS.stepOnDecline}
        onChange={(next) => onChange({ ...step, on_decline: next })}
        disabled={disabled}
        multiline
        expandable
        error={errorFor(fieldErrors, path('on_decline'))}
      />
      <CounterField
        id={`agentic-step-${step.step_id}-on-ineligible`}
        label={t('config.editAgentic.scripts.onIneligible')}
        value={step.on_ineligible}
        maxLength={AGENT_CONFIG_MAX_LENGTHS.stepOnIneligible}
        onChange={(next) => onChange({ ...step, on_ineligible: next })}
        disabled={disabled}
        multiline
        expandable
        error={errorFor(fieldErrors, path('on_ineligible'))}
      />
      <CounterField
        id={`agentic-step-${step.step_id}-on-consent-not-classified`}
        label={t('config.editAgentic.scripts.onConsentNotClassified')}
        value={step.on_consent_not_classified}
        maxLength={AGENT_CONFIG_MAX_LENGTHS.stepOnConsentNotClassified}
        onChange={(next) => onChange({ ...step, on_consent_not_classified: next })}
        disabled={disabled}
        multiline
        expandable
        error={errorFor(fieldErrors, path('on_consent_not_classified'))}
      />
      <CounterField
        id={`agentic-step-${step.step_id}-retry-guidance`}
        label={t('config.editAgentic.scripts.retryGuidance')}
        value={step.retry_guidance}
        maxLength={AGENT_CONFIG_MAX_LENGTHS.stepRetryGuidance}
        onChange={(next) => onChange({ ...step, retry_guidance: next })}
        disabled={disabled}
        multiline
        expandable
        rows={3}
        error={errorFor(fieldErrors, path('retry_guidance'))}
      />

      <div className="flex justify-between">
        <button
          type="button"
          data-testid={`agentic-step-${step.step_id}-remove`}
          disabled={disabled}
          onClick={onRemove}
          className={textActionDangerClass}
        >
          {t('config.editAgentic.scripts.removeStep')}
        </button>
        <button
          type="button"
          data-testid={`agentic-step-${step.step_id}-close`}
          onClick={onClose}
          className={textActionClass}
        >
          {t('config.editAgentic.facts.closeEditing')}
        </button>
      </div>
    </div>
  );
};
